/**
 * As rotas internas, fabricadas a partir da **mesma** lista que desenha o menu.
 *
 * Escrever a arvore a mao ao lado de `ITENS_DE_NAVEGACAO` seria manter duas listas: um item
 * novo no menu apontaria para uma rota inexistente (404 dentro do proprio shell), ou uma rota
 * existiria sem nunca aparecer no menu. Aqui as duas coisas nascem juntas.
 *
 * Os caminhos sao os de **todos** os perfis, e nao os do perfil de quem entrou: a rota tem de
 * existir mesmo para quem nao ve o item, porque quem recusa e o backend, nao o menu. Um SDR
 * que digite `/configuracao` deve encontrar a tela e ser recusado pelo servico — nao cair num
 * 404 que sugere que a funcionalidade nao existe.
 *
 * Cada tela **ainda sem conteudo** e um placeholder: o cabecalho do shell ja traz o titulo e o
 * subtitulo do prototipo, e o corpo diz de qual epico o conteudo vem.
 *
 * As que ja tem conteudo saem deste laco e ganham arquivo proprio, sem sair de
 * `CAMINHOS_INTERNOS`: o item de menu e a rota continuam nascendo da **mesma** lista, e o que
 * muda e so quem desenha o corpo. `/administracao` (hoje `/configuracao`, 11.2) foi a primeira
 * (historia 2.3) e `/empresas` a segunda (2.6); quem entrar aqui para acrescentar a proxima
 * acrescenta o caminho a
 * `COM_TELA_PROPRIA` e registra a rota nova em `roteador.ts`, e nao duas listas paralelas.
 * `/hoje` foi a quarta (historia 3.9) — e a que mais importava: ela e a primeira tela depois
 * do login, e ate ali era um placeholder dizendo que o conteudo vinha no Epico 4. `/originados`
 * e a quinta (3.11): ela **nasce** fora do laco, porque o caminho e novo — e nao um placeholder
 * que virou tela. `/tarefas` e a sexta (4.6) e `/relatorios` a setima (7.2), e as duas sao do
 * primeiro tipo: os dois caminhos existiam como placeholder desde a 1.4, e o que mudou foi so quem
 * desenha o corpo. `/whatsapp` e a oitava (8.1), e do mesmo tipo: ela era placeholder desde a 1.4 e
 * passou a montar o cartao do proprio numero. `/integracoes` (11.2) nasce fora do laco, como
 * `/originados`: o caminho e novo. `/pessoas` (13.4) tambem nasce fora do laco, pelo mesmo motivo.
 *
 * **Com ela, o laco ficou vazio** — e a lista continua existindo, e continua sendo derivada de
 * `CAMINHOS_INTERNOS`, porque o laco e a fabrica de placeholder do **proximo** caminho: um item de
 * menu novo nasce com rota, e nao com um 404 dentro do proprio shell. O que `telas.test.ts` prende
 * e exatamente isso, por igualdade: se `COM_TELA_PROPRIA` perder uma entrada, aquele caminho volta
 * a ser registrado duas vezes e o teste reprova.
 */

import { createRoute } from '@tanstack/react-router'

import { TelaPlaceholder } from '@/shell/Shell'
import { CAMINHOS, CAMINHOS_INTERNOS, type CaminhoInterno } from '@/shell/navegacao'
import { rotaDaAplicacao } from './aplicacao'

/**
 * Os caminhos internos que **nao** sao placeholder: a rota deles mora em arquivo proprio.
 *
 * Sem esta lista, cada um deles ganharia duas rotas com o mesmo caminho na arvore — a do laco
 * e a do arquivo proprio — e qual delas o roteador escolheria seria detalhe de ordem de
 * declaracao, e nao decisao de ninguem.
 */
const COM_TELA_PROPRIA: readonly CaminhoInterno[] = [
  CAMINHOS.aguardandoEntrega,
  CAMINHOS.configuracao,
  CAMINHOS.empresas,
  CAMINHOS.funil,
  CAMINHOS.hoje,
  CAMINHOS.integracoes,
  CAMINHOS.originados,
  CAMINHOS.pessoas,
  CAMINHOS.relatorios,
  CAMINHOS.tarefas,
  CAMINHOS.whatsapp,
]

export const rotasDasTelas = CAMINHOS_INTERNOS.filter(
  (caminho) => !COM_TELA_PROPRIA.includes(caminho),
).map((caminho) =>
  createRoute({
    getParentRoute: () => rotaDaAplicacao,
    path: caminho,
    component: TelaPlaceholder,
  }),
)
